import type { ComplianceItemStatus } from './complianceChecklists';

/**
 * Shared operator workflow for every NextCompli compliance console
 * (website, education, DoD, business formation, and future consoles that reuse this panel).
 *
 * DoD checklist copy, insurance wording, SAM wait, COI lock, and planMin
 * stay in complianceChecklists.ts. This module only names the lanes and
 * the strategies those consoles already learned — it does not rewrite
 * DoD item text.
 */
export const COMPLIANCE_WORKFLOW_GUARDRAILS = {
  lanes: ['ready', 'blocked', 'done'] as const,
  statusNotColorOnly: true,
  externalWaitIsBlocked: true,
  laterUntilPriorIsBlocked: true,
  proofBeforeComplete: true,
  neverInventGovernmentIds: true,
  hideProgressUntilCompanySelected: true,
  hideSaveUntilCompanySelected: true,
  companyNameBeforeQuestions: true,
  glossaryFirstUseOnly: true,
  bilingual: true,
  fileProofWhenVerifySteps: true,
  planLadderGating: true,
  /** Shared console chrome — learned from DoD, required on every checklist console. */
  workbenchShell: true,
  companySelectNotFreeText: true,
  nextStepsBeforeChecklist: true,
  collapseProgramNotices: true,
  hideSubtitleOnHome: true,
  hideHowToUntilAsked: true,
  progressThenDoneThenNextSteps: true,
  oneRecommendedAction: true,
  scanAndToolsAfterNextSteps: true,
  /** New console sections and platform lists ship as collapsed accordions; expand on demand. */
  accordionsCollapsedByDefault: true,
} as const;

export type WorkflowLane = (typeof COMPLIANCE_WORKFLOW_GUARDRAILS.lanes)[number];

export const WORKFLOW_LANES: readonly WorkflowLane[] = COMPLIANCE_WORKFLOW_GUARDRAILS.lanes;

export function laneForVerifyStep(args: {
  done: boolean;
  waitingOnExternal: boolean;
  isCurrent: boolean;
}): WorkflowLane {
  if (args.done) return 'done';
  if (args.waitingOnExternal || !args.isCurrent) return 'blocked';
  return 'ready';
}

export function laneForItemStatus(status: ComplianceItemStatus): WorkflowLane | 'idle' {
  if (status === 'reviewed') return 'done';
  if (status === 'gap') return 'blocked';
  if (status === 'in_progress') return 'ready';
  return 'idle';
}

export function workflowLaneLabel(lane: WorkflowLane, language: 'en' | 'es'): string {
  const labels: Record<WorkflowLane, { en: string; es: string }> = {
    ready: { en: 'Next', es: 'Siguiente' },
    blocked: { en: 'Blocked', es: 'Bloqueado' },
    done: { en: 'Done', es: 'Hecho' },
  };
  return language === 'es' ? labels[lane].es : labels[lane].en;
}

export function workflowLaneHint(
  lane: WorkflowLane,
  language: 'en' | 'es',
  kind?: string,
): string {
  const website = kind === 'website-compliance';
  const hints: Record<WorkflowLane, { en: string; es: string }> = {
    ready: { en: 'You can do this step now', es: 'Puede hacer este paso ahora' },
    blocked: { en: 'Waiting on a prior step or an outside office', es: 'Espera un paso anterior o una oficina externa' },
    done: website
      ? { en: 'This check is compliant', es: 'Este control cumple' }
      : { en: 'Proof is on file', es: 'La prueba está en archivo' },
  };
  return language === 'es' ? hints[lane].es : hints[lane].en;
}
